import React, { useEffect, useMemo, useState } from "react";
import ListTotalsFooter from "../../../components/ListTotalsFooter";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Box,
  Button,
  Typography,
  TextField,
  Tooltip,
  Paper,
  Grid,
  Stack,
} from "@mui/material";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PriceCheckOutlinedIcon from "@mui/icons-material/PriceCheckOutlined";
import { FileDownload } from "@mui/icons-material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";

import { apiData } from "../../../services/api_service";
import { fetchFocReportAPI } from "../../../config";
import Layout from "../../../layout/Layout";
import { formatAmount, formatNumber, formatAED } from "../../../utils/format";
import ListFilterBar, { emptyListFilters } from "../../../components/ListFilterBar";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

// FOC Given List / Report (Item 8) — every approved, actually-invoiced FOC
// line item, filterable by date range. Only reads tbl_order_items where
// is_foc = 1 (see models/focModel.js#getFocReport on the backend) — pending
// or rejected FOC requests never show up here, only what was really given.
const FocGivenReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Defaults to the current month so the report opens showing something
  // useful instead of an unfiltered dump of every FOC line item ever given.
  const [fromDate, setFromDate] = useState(() => dayjs().startOf("month"));
  const [toDate, setToDate] = useState(() => dayjs().endOf("month"));
  // Store / Customer / Salesperson / Payment mode / Payment status filters
  const [bar, setBar] = useState({ ...emptyListFilters });

  const fetchReport = async (overrides = {}) => {
    try {
      setLoading(true);
      const effectiveFromDate = "fromDate" in overrides ? overrides.fromDate : fromDate;
      const effectiveToDate = "toDate" in overrides ? overrides.toDate : toDate;
      const payload = {
        fromDate: effectiveFromDate ? dayjs(effectiveFromDate).format("YYYY-MM-DD") : "",
        toDate: effectiveToDate ? dayjs(effectiveToDate).format("YYYY-MM-DD") : "",
        store_id: Number(bar.storeId) > 0 ? bar.storeId : undefined,
        customer_id: Number(bar.customerId) > 0 ? bar.customerId : undefined,
        sales_person_id: Number(bar.salesPersonId) > 0 ? bar.salesPersonId : undefined,
        payment_method: bar.paymentMode || undefined,
        payment_status: bar.paymentStatus || undefined,
      };
      const res = await apiData("get", fetchFocReportAPI, payload);
      const list = res?.data?.rows;
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch FOC report", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bar]);

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    fetchReport({ fromDate: null, toDate: null });
  };

  const handleExportData = (table) => {
    // Filtered AND sorted, i.e. the same rows in the same order as the screen.
    const visibleRows = table.getPrePaginationRowModel().rows.map((r) => r.original);
    if (!visibleRows.length) {
      alert("No data to export!");
      return;
    }
    const exportData = visibleRows.map((item, index) => ({
      "#": index + 1,
      Date: item.order_date ? dayjs(item.order_date).format("DD-MM-YYYY") : "",
      "Invoice No.": item.order_number || "",
      "Customer / Company": item.customer_name || "",
      "Product / Item": item.item_name || "",
      Quantity: item.quantity || 0,
      "Value of FOC": Number(item.foc_value) || 0,
      "Buying Price (Unit)": Number(item.buying_price) || 0,
      "Buying Cost (Total)":
        (Number(item.buying_price) || 0) * (Number(item.quantity) || 0),
      "Remarks / Reason": item.foc_reason || "",
    }));
    // Totals row, matching the summary shown on screen.
    exportData.push({
      "#": "",
      Date: "",
      "Invoice No.": "TOTAL",
      "Customer / Company": "",
      "Product / Item": "",
      Quantity: visibleRows.reduce((a, r) => a + (Number(r.quantity) || 0), 0),
      "Value of FOC": visibleRows.reduce((a, r) => a + (Number(r.foc_value) || 0), 0),
      "Buying Price (Unit)": "",
      "Buying Cost (Total)": visibleRows.reduce(
        (a, r) => a + (Number(r.buying_price) || 0) * (Number(r.quantity) || 0),
        0
      ),
      "Remarks / Reason": "",
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "FOC_Given_Report");
    writeXlsxFrozen(XLSX, wb, `FOC_Given_Report_${dayjs().format("DD_MM_YYYY")}.xlsx`);
  };

  const summary = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.lines += 1;
          acc.quantity += Number(r.quantity) || 0;
          acc.value += Number(r.foc_value) || 0;
          // Actual cost to the business, not the customer-facing value —
          // buying_price × quantity is what giving these lines away really
          // costs, which matters more here than the selling-price total.
          acc.buyingCost += (Number(r.buying_price) || 0) * (Number(r.quantity) || 0);
          return acc;
        },
        { lines: 0, quantity: 0, value: 0, buyingCost: 0 }
      ),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "order_date",
        header: "Date",
        size: 120,
        filterVariant: "text",
        Cell: ({ cell }) =>
          cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
      },
      {
        accessorKey: "order_number",
        header: "Invoice No.",
        size: 130,
        filterVariant: "text",
        Cell: ({ row }) => (
          <Typography
            component="a"
            href={`/invoice/edit/${row.original.order_id}`}
            sx={{ color: "primary.main", fontWeight: 600, textDecoration: "none" }}
          >
            {row.original.order_number}
          </Typography>
        ),
      },
      {
        accessorKey: "customer_name",
        header: "Customer / Company Name",
        size: 220,
        filterVariant: "text",
      },
      {
        accessorKey: "item_name",
        header: "Product / Item",
        size: 220,
        filterVariant: "text",
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        size: 100,
        filterVariant: "text",
        Cell: ({ row }) => formatNumber(row.original.quantity),
        muiTableBodyCellProps: { align: "right" },
      },
      {
        accessorKey: "foc_value",
        header: "Value of FOC",
        size: 140,
        filterVariant: "text",
        Cell: ({ cell }) => `${formatAED(cell.getValue())}`,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        // Never shown on the printed invoice/CRN itself — a FOC line prints
        // with no unit price at all there. This report is the one place
        // that's meant to surface it, since it's what approval/audit
        // actually needs to see: what a "free" line really cost the
        // business, not what it would have sold for.
        accessorKey: "buying_price",
        header: "Buying Price (Unit)",
        size: 140,
        filterVariant: "text",
        Cell: ({ cell }) => `${formatAED(cell.getValue())}`,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        id: "buying_cost_total",
        header: "Buying Cost (Total)",
        size: 150,
        Cell: ({ row }) =>
          `${formatAED(
            (Number(row.original.buying_price) || 0) *
              (Number(row.original.quantity) || 0)
          )}`,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        accessorKey: "foc_reason",
        header: "Remarks / Reason",
        size: 260,
        filterVariant: "text",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
    ],
    []
  );

  const KPI_CARDS = [
    { label: "FOC Line Items", value: summary.lines, prefix: "", decimals: 0, icon: ReceiptLongOutlinedIcon, accent: "#64748b" },
    { label: "Total Quantity Given", value: summary.quantity, prefix: "", decimals: 0, icon: Inventory2OutlinedIcon, accent: "#0c54a0" },
    { label: "Total FOC Value", value: summary.value, prefix: "AED ", decimals: 2, icon: CardGiftcardOutlinedIcon, accent: "#d32f2f" },
    // Not shown on the invoice itself — a FOC line prints with no unit
    // price there — but this is the number that matters for approval/
    // audit: what giving these lines away actually cost the business.
    { label: "Total Buying Cost", value: summary.buyingCost, prefix: "AED ", decimals: 2, icon: PriceCheckOutlinedIcon, accent: "#b8860b" },
  ];

  return (
    <Layout>
      <Box sx={{ pt: "85px", px: { xs: 2, md: 3.5 }, pb: 4 }}>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                FOC Given List / Report
              </Typography>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 0.25 }}>
                <Link underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </Link>
                <Typography color="text.primary">FOC Given Report</Typography>
              </Breadcrumbs>
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Back
            </Button>
          </Box>

          <Grid container spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label="From Date"
                  value={fromDate}
                  inputFormat="DD-MM-YYYY"
                  onChange={(newValue) => setFromDate(newValue)}
                  renderInput={(params) => (
                    <Tooltip title="Include FOC items given from this date (leave blank for all-time)" arrow>
                      <TextField {...params} size="small" fullWidth />
                    </Tooltip>
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label="To Date"
                  value={toDate}
                  inputFormat="DD-MM-YYYY"
                  onChange={(newValue) => setToDate(newValue)}
                  renderInput={(params) => (
                    <Tooltip title="Include FOC items given up to this date (leave blank for up-to-date)" arrow>
                      <TextField {...params} size="small" fullWidth />
                    </Tooltip>
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => fetchReport()}
                  disabled={loading}
                  sx={{ textTransform: "none", borderRadius: 2, minWidth: 90 }}
                >
                  {loading ? "Loading..." : "Search"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  sx={{ textTransform: "none", borderRadius: 2, minWidth: 80 }}
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* <Box
          sx={{
            mt: 1.5,
            mb: 2,
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(3, 1fr)" },
          }}
        >
          {KPI_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <Paper
                key={card.label}
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 3,
                  overflow: "hidden",
                  borderLeft: `4px solid ${card.accent}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                  <Icon sx={{ fontSize: 16, color: card.accent }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {card.label}
                  </Typography>
                </Stack>
                <Typography variant="subtitle1" fontWeight={700}>
                  {card.prefix}
                  {formatAmount(card.value, { decimals: card.decimals })}
                </Typography>
              </Paper>
            );
          })}
        </Box> */}

        <ListFilterBar
          value={bar}
          onChange={setBar}
          onClear={() => setBar({ ...emptyListFilters })}
          showStore={false}
          showSalesPerson={false}
          showDates={false}
          showSearch={false}
        />

        <MaterialReactTable
          columns={columns}
          data={rows}
          state={{ isLoading: loading }}
          enableGlobalFilter={false}
          enableColumnFilters
          enableFullScreenToggle={false}
          enableStickyHeader
          enableRowNumbers
          rowNumberMode="original"
          initialState={{
            sorting: [{ id: "order_date", desc: true }],
            pagination: { pageIndex: 0, pageSize: 25 },
            showColumnFilters: true,
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Tooltip title="Export data to excel sheet" arrow>
              <Button
                onClick={() => handleExportData(table)}
                startIcon={<FileDownload />}
                sx={{ background: "#1a73e829", textTransform: "none" }}
              >
                Export To Excel
              </Button>
            </Tooltip>
          )}
          muiTablePaperProps={{ variant: "outlined", elevation: 0, sx: { borderRadius: 3, overflow: "hidden" } }}
          muiTableContainerProps={{
            sx: { maxHeight: "calc(100vh - 300px)", minHeight: 450, overflowY: "auto" },
          }}
          renderBottomToolbarCustomActions={({ table }) => {
            const visible = table.getFilteredRowModel().rows.map((r) => r.original);
            const t = visible.reduce(
              (acc, r) => {
                acc.lines += 1;
                acc.quantity += Number(r.quantity) || 0;
                acc.value += Number(r.foc_value) || 0;
                return acc;
              },
              { lines: 0, quantity: 0, value: 0 }
            );
            return (
              <ListTotalsFooter
                items={[
                  { label: "FOC Line Items", value: formatNumber(t.lines) },
                  { label: "Total Quantity", value: formatNumber(t.quantity) },
                  { label: "Total FOC Value", value: `${formatAED(t.value)}`, tone: "purple" },
                ]}
              />
            );
          }}
        />
      </Box>
    </Layout>
  );
};

export default FocGivenReport;
